import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Log, Subject } from '../../types/log';

type CalendarView = 'month' | 'week' | 'day';

interface LogCalendarProps {
  logs: Log[];
  subjects?: Subject[];
  selectedSubjectIds?: string[];
  onSelectLog: (logId: string) => void;
}

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTimeFromTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const parseTimeToMinutes = (timeStr: string | null): number | null => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

const hours = Array.from({ length: 24 }, (_, i) => i);

interface CalendarDay {
  date: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  logs: Log[];
}

const LogCalendar: React.FC<LogCalendarProps> = ({ logs, subjects = [], selectedSubjectIds = [], onSelectLog }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<CalendarView>('month');
  const [currentTime, setCurrentTime] = useState(new Date());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if ((viewMode === 'week' || viewMode === 'day') && scrollContainerRef.current) {
      const now = currentTime.getHours();
      const scrollPosition = (now * 60) - 100;
      scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [viewMode, currentTime]);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getSubjectColor = (subjectId: string | undefined): string => {
    if (!subjectId) return '#3b82f6';
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.color || '#3b82f6';
  };

  const getLogBorderColor = (log: Log): string => {
    if (log.subjects && log.subjects.length > 0) {
      return log.subjects[0].color || getSubjectColor(log.subjects[0].id);
    }
    return '#3b82f6';
  };

  const filteredLogs = useMemo(() => {
    if (selectedSubjectIds.length === 0) return logs;
    return logs.filter(log => {
      if (!log.subjects || log.subjects.length === 0) return false;
      return log.subjects.some(s => selectedSubjectIds.includes(s.id));
    });
  }, [logs, selectedSubjectIds]);

  const logsByDate = useMemo(() => {
    const map: Record<string, Log[]> = {};
    filteredLogs.forEach((log) => {
      if (log.startTime) {
        const dateKey = formatLocalDate(new Date(log.startTime));
        if (!map[dateKey]) {
          map[dateKey] = [];
        }
        map[dateKey].push(log);
      }
    });
    return map;
  }, [filteredLogs]);

  const weekDaysData = useMemo(() => {
    const today = new Date(currentDate);
    const dayOfWeek = today.getDay();
    const days: CalendarDay[] = [];
    const todayStr = formatLocalDate(new Date());

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - dayOfWeek + i);
      const dateStr = formatLocalDate(date);
      days.push({
        date: dateStr,
        dayOfMonth: date.getDate(),
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        logs: logsByDate[dateStr] || [],
      });
    }

    return days;
  }, [currentDate, logsByDate]);

  const monthCalendarDays = useMemo((): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: CalendarDay[] = [];
    const todayStr = formatLocalDate(new Date());

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const dayNum = prevMonthLastDay - i;
      const date = new Date(year, month - 1, dayNum);
      const dateStr = formatLocalDate(date);
      days.push({
        date: dateStr,
        dayOfMonth: dayNum,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        logs: logsByDate[dateStr] || [],
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatLocalDate(date);
      days.push({
        date: dateStr,
        dayOfMonth: day,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        logs: logsByDate[dateStr] || [],
      });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      const dateStr = formatLocalDate(date);
      days.push({
        date: dateStr,
        dayOfMonth: day,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        logs: logsByDate[dateStr] || [],
      });
    }

    return days;
  }, [currentDate, logsByDate]);

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (day: CalendarDay) => {
    setSelectedDate(day.date);
  };

  const handleViewModeChange = (mode: CalendarView) => {
    setViewMode(mode);
  };

  const getViewTitle = () => {
    if (viewMode === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (viewMode === 'week') {
      const weekStart = weekDaysData[0];
      const weekEnd = weekDaysData[6];
      const startDate = new Date(weekStart.date);
      const endDate = new Date(weekEnd.date);
      if (startDate.getMonth() === endDate.getMonth()) {
        return `${monthNames[startDate.getMonth()]} ${startDate.getDate()} - ${endDate.getDate()}, ${startDate.getFullYear()}`;
      }
      return `${monthNames[startDate.getMonth()]} ${startDate.getDate()} - ${monthNames[endDate.getMonth()]} ${endDate.getDate()}, ${endDate.getFullYear()}`;
    } else {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    }
  };

  const isToday = (dateStr: string) => {
    return dateStr === formatLocalDate(new Date());
  };

  const getCurrentTimePosition = (): number => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  const selectedDayLogs = selectedDate ? logsByDate[selectedDate] || [] : [];

  const getPriorityColor = (priority: number) => {
    if (priority >= 3) return 'bg-red-500';
    if (priority >= 2) return 'bg-yellow-500';
    if (priority >= 1) return 'bg-green-500';
    return 'bg-gray-500';
  };

  const closeModal = () => {
    setSelectedDate(null);
  };

  const renderLogBlock = (log: Log) => {
    const timeStr = log.startTime ? formatTimeFromTimestamp(log.startTime) : null;
    const startMinutes = timeStr ? parseTimeToMinutes(timeStr) : null;
    const top = startMinutes !== null ? startMinutes : 0;
    const height = 45;

    const isAllDay = startMinutes === null;
    const borderColor = getLogBorderColor(log);

    return (
      <div
        key={log.id}
        onClick={() => onSelectLog(log.id)}
        className={`
          absolute left-1 right-1 z-10 p-1 rounded text-xs cursor-pointer overflow-hidden
          ${log.status === 'completed' ? 'opacity-50' : ''}
        `}
        style={{
          top: `${top + 4}px`,
          height: isAllDay ? '24px' : `${height}px`,
          backgroundColor: `${borderColor}20`,
          borderLeft: `3px solid ${borderColor}`,
        }}
      >
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: borderColor }} />
          <span className="truncate dark:text-claude-darkText text-claude-text font-medium">
            {log.title}
          </span>
        </div>
        {timeStr && !isAllDay && (
          <div className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {timeStr}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div className="shrink-0 flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
            {getViewTitle()}
          </h2>
          <button
            type="button"
            onClick={handleToday}
            className="px-2 py-1 text-xs font-medium rounded border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
          >
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
            {(['month', 'week', 'day'] as CalendarView[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => handleViewModeChange(mode)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
                  viewMode === mode
                    ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover dark:text-claude-darkText text-claude-text'
                    : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:text-claude-darkText hover:text-claude-text'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 ml-2">
            <button
              type="button"
              onClick={handlePrev}
              className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {viewMode === 'month' && (
          <div className="p-6 overflow-auto h-full">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {monthCalendarDays.map((day, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className={`
                    relative min-h-[80px] p-2 rounded-lg border transition-colors text-left
                    ${day.isCurrentMonth
                      ? 'dark:border-claude-darkBorder border-claude-border'
                      : 'dark:border-claude-darkBorder/50 border-claude-border/50 opacity-50'
                    }
                    ${day.isToday ? 'ring-2 ring-claude-accent' : ''}
                    hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover
                  `}
                >
                  <span
                    className={`
                      text-sm font-medium
                      ${day.isCurrentMonth
                        ? 'dark:text-claude-darkText text-claude-text'
                        : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                      }
                      ${day.isToday ? 'text-claude-accent' : ''}
                    `}
                  >
                    {day.dayOfMonth}
                  </span>

                  {day.logs.length > 0 && (
                    <div className="mt-1 space-y-1">
                      {day.logs.slice(0, 3).map((log) => (
                        <div
                          key={log.id}
                          className={`
                            flex items-center gap-1 px-1.5 py-0.5 rounded text-xs truncate
                            ${log.status === 'completed'
                              ? 'dark:bg-claude-darkSurface bg-claude-surface line-through opacity-50'
                              : 'dark:bg-claude-darkSurface bg-claude-surface'
                            }
                          `}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getPriorityColor(log.priority)}`} />
                          <span className="truncate dark:text-claude-darkTextSecondary text-claude-textSecondary">
                            {log.title}
                          </span>
                        </div>
                      ))}
                      {day.logs.length > 3 && (
                        <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                          +{day.logs.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'week' && (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex border-b dark:border-claude-darkBorder border-claude-border flex-shrink-0">
              <div className="w-14 flex-shrink-0" />
              {weekDaysData.map((day, index) => (
                <div
                  key={index}
                  className={`
                    flex-1 text-center py-2 border-r dark:border-claude-darkBorder border-claude-border last:border-r-0
                    ${day.isToday ? 'bg-claude-accent/10' : ''}
                  `}
                >
                  <div className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    {weekDays[new Date(day.date).getDay()]}
                  </div>
                  <div className={`
                    text-lg font-semibold
                    ${day.isToday ? 'text-claude-accent' : 'dark:text-claude-darkText text-claude-text'}
                  `}>
                    {day.dayOfMonth}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-1 overflow-hidden">
              <div ref={scrollContainerRef} className="h-full overflow-y-auto">
                <div className="flex relative" style={{ height: `${24 * 60}px` }}>
                  <div className="w-14 flex-shrink-0 relative">
                    {hours.map((hour) => (
                      <div
                        key={hour}
                        className="absolute left-0 right-0 text-xs text-right pr-2 dark:text-claude-darkTextSecondary text-claude-textSecondary"
                        style={{ top: `${hour * 60}px`, height: '60px' }}
                      >
                        <span className="block pt-1">
                          {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                        </span>
                      </div>
                    ))}
                  </div>

                  {weekDaysData.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={`
                        flex-1 relative
                        ${day.isToday ? 'bg-claude-accent/5' : ''}
                      `}
                      style={{ height: `${24 * 60}px` }}
                    >
                      {hours.map((hour) => (
                        <div
                          key={hour}
                          className="absolute left-0 right-0 border-b dark:border-claude-darkBorder/30 border-claude-border/30"
                          style={{ top: `${hour * 60}px`, height: '60px' }}
                        />
                      ))}
                      {day.logs.map((log) => renderLogBlock(log))}
                    </div>
                  ))}

                  {weekDaysData.some(d => d.isToday) && (
                    <div
                      className="absolute left-14 right-0 z-20 pointer-events-none"
                      style={{ top: `${getCurrentTimePosition() + 4}px` }}
                    >
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                        <div className="flex-1 h-0.5 bg-red-500" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'day' && (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="border-b dark:border-claude-darkBorder border-claude-border p-4 text-center">
              <div className="text-sm font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {weekDays[currentDate.getDay()]}
              </div>
              <div className={`
                text-3xl font-bold
                ${isToday(formatLocalDate(currentDate)) ? 'text-claude-accent' : 'dark:text-claude-darkText text-claude-text'}
              `}>
                {currentDate.getDate()}
              </div>
              <div className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </div>
            </div>

            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 relative" style={{ height: `${24 * 60}px` }}>
                <div className="col-span-1" />

                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="absolute left-0 right-0 border-b dark:border-claude-darkBorder/30 border-claude-border/30"
                    style={{ top: `${hour * 60}px`, height: '60px' }}
                  />
                ))}

                <div className="absolute left-0 w-14">
                  {hours.map((hour) => (
                    <div
                      key={hour}
                      className="h-[60px] text-xs text-right pr-2 pt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary"
                    >
                      {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                    </div>
                  ))}
                </div>

                <div
                  className={`
                    absolute left-14 right-0 border-b dark:border-claude-darkBorder border-claude-border
                    ${isToday(formatLocalDate(currentDate)) ? 'bg-claude-accent/5' : ''}
                  `}
                  style={{ top: 0, height: `${24 * 60}px` }}
                >
                  {(logsByDate[formatLocalDate(currentDate)] || []).map((log) => renderLogBlock(log))}
                </div>

                {isToday(formatLocalDate(currentDate)) && (
                  <div
                    className="absolute left-14 right-0 z-20 pointer-events-none"
                    style={{ top: `${getCurrentTimePosition() + 4}px` }}
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                      <div className="flex-1 h-0.5 bg-red-500" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative w-full max-w-md mx-4 bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b dark:border-claude-darkBorder border-claude-border">
              <h3 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
                {new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                <XMarkIcon className="h-5 w-5 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {selectedDayLogs.length === 0 ? (
                <p className="text-center py-8 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  No logs for this day
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedDayLogs.map((log) => (
                    <button
                      key={log.id}
                      type="button"
                      onClick={() => {
                        onSelectLog(log.id);
                        closeModal();
                      }}
                      className={`
                        w-full text-left p-3 rounded-lg border transition-colors
                        ${log.status === 'completed'
                          ? 'dark:border-claude-darkBorder border-claude-border opacity-60'
                          : 'dark:border-claude-darkBorder border-claude-border hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
                        }
                      `}
                    >
                      <div className="flex items-start gap-2">
                        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getPriorityColor(log.priority)}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium dark:text-claude-darkText text-claude-text ${log.status === 'completed' ? 'line-through' : ''}`}>
                            {log.title}
                          </div>
                          {log.startTime && (
                            <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-0.5">
                              {formatTimeFromTimestamp(log.startTime)}
                              {log.endTime && ` - ${formatTimeFromTimestamp(log.endTime)}`}
                            </div>
                          )}
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs px-1.5 py-0.5 rounded dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkTextSecondary text-claude-textSecondary capitalize">
                              {log.category}
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${
                              log.priority >= 3 ? 'bg-red-500/10 text-red-500' :
                              log.priority >= 2 ? 'bg-yellow-500/10 text-yellow-500' :
                              log.priority >= 1 ? 'bg-green-500/10 text-green-500' :
                              'bg-gray-500/10 text-gray-500'
                            }`}>
                              priority {log.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogCalendar;
